class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.showAddedPopup(parsedState);
    });
  }

  showAddedPopup(parsedState) {
    this.closeAddedPopup();

    const previouslyFocused = document.activeElement;
    const popup = document.createElement('div');
    popup.className = 'added-popup';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-modal', 'true');
    popup.setAttribute('dir', 'rtl');

    const overlay = document.createElement('div');
    overlay.className = 'added-popup__overlay';

    const box = document.createElement('div');
    box.className = 'added-popup__box';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'added-popup__close';
    closeButton.setAttribute('aria-label', 'סגירה');
    closeButton.innerHTML = '&times;';

    const title = document.createElement('p');
    title.className = 'added-popup__title';
    title.textContent = '✓ המוצר נוסף לעגלה';

    const product = document.createElement('div');
    product.className = 'added-popup__product';

    const imageUrl = parsedState.featured_image?.url || parsedState.image;
    if (imageUrl) {
      const img = document.createElement('img');
      img.className = 'added-popup__image';
      img.src = imageUrl.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2');
      img.onerror = () => {
        img.onerror = null;
        img.src = imageUrl;
      };
      img.alt = parsedState.product_title || '';
      img.width = 72;
      img.height = 72;
      product.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'added-popup__info';
    const name = document.createElement('p');
    name.className = 'added-popup__name';
    name.textContent = parsedState.product_title || parsedState.title || '';
    info.appendChild(name);
    if (parsedState.variant_title) {
      const variant = document.createElement('p');
      variant.className = 'added-popup__variant';
      variant.textContent = parsedState.variant_title;
      info.appendChild(variant);
    }
    product.appendChild(info);

    const actions = document.createElement('div');
    actions.className = 'added-popup__actions';

    const buyNow = document.createElement('a');
    buyNow.className = 'button button--primary button--full-width';
    buyNow.href = '/checkout';
    buyNow.target = '_top';
    buyNow.textContent = 'קנה עכשיו';

    const continueButton = document.createElement('button');
    continueButton.type = 'button';
    continueButton.className = 'button button--secondary button--full-width';
    continueButton.textContent = 'המשך לקנות';

    actions.appendChild(buyNow);
    actions.appendChild(continueButton);

    box.appendChild(closeButton);
    box.appendChild(title);
    box.appendChild(product);
    box.appendChild(actions);
    popup.appendChild(overlay);
    popup.appendChild(box);
    document.body.appendChild(popup);
    document.body.classList.add('overflow-hidden');

    const close = () => {
      this.closeAddedPopup();
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
    this.addedPopupKeyHandler = (evt) => evt.key === 'Escape' && close();
    document.addEventListener('keydown', this.addedPopupKeyHandler);

    overlay.addEventListener('click', close);
    closeButton.addEventListener('click', close);
    continueButton.addEventListener('click', close);

    requestAnimationFrame(() => popup.classList.add('active'));
    buyNow.focus();
  }

  closeAddedPopup() {
    const existing = document.querySelector('.added-popup');
    if (!existing) return;
    existing.remove();
    document.body.classList.remove('overflow-hidden');
    if (this.addedPopupKeyHandler) {
      document.removeEventListener('keydown', this.addedPopupKeyHandler);
      this.addedPopupKeyHandler = null;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
